import { useEffect, useState } from "react";
import Icon from "@mdi/react";
import { mdiCheck } from "@mdi/js";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { setLines, setCurrentStep } from "reducer";
import "./Configurator.scss";
import Navbar from "components/navbar/Navbar";
import StepsConfig from "./steps-config/StepsConfig";
import ConfigurationCode from "components/configuration-code/ConfigurationCode";
import ConfigurationPanel from "./configuraton-panel/ConfigurationPanel";
import linesConfigurationOptions from "db/line_configuration_options.json";

const Configurator = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const currentStep = useSelector(({ currentStep }) => currentStep);
  const circles = useSelector(({ circles }) => circles);
  const hydraulicLines = useSelector(({ lines }) => lines.hydraulic);
  const electricLines = useSelector(({ lines }) => lines.electric);
  const isHydraulicSet = useSelector(
    ({ is_hydraulic_set }) => is_hydraulic_set
  );
  const isElectricSet = useSelector(({ is_electric_set }) => is_electric_set);
  const [selectedHydraulicLines, setSelectedHydraulicLines] = useState(
    hydraulicLines || 0
  );
  const [selectedElectricLines, setSelectedElectricLines] = useState(
    electricLines || 0
  );
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 991);

  const totalSlots = 10;
  const maxHydraulicLines = Math.max(0, totalSlots - selectedElectricLines);
  const hydraulicSelects = linesConfigurationOptions["hydraulic"] || [];

  useEffect(() => {
    setSelectedHydraulicLines(hydraulicLines || 0);
  }, [hydraulicLines]);

  useEffect(() => {
    setSelectedElectricLines(electricLines || 0);
  }, [electricLines]);

  useEffect(() => {
    if (selectedHydraulicLines > maxHydraulicLines) {
      const safeValue = Math.max(maxHydraulicLines, 0);
      setSelectedHydraulicLines(safeValue);
      dispatch(setLines("hydraulic", safeValue));
    }
  }, [maxHydraulicLines, selectedHydraulicLines, dispatch]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentStep]);

  // Setta automaticamente il flag electric quando si arriva allo step 3
  // Questo risolve il bug quando l'utente non clicca su "0" perché è già selezionato
  useEffect(() => {
    if (currentStep === 3 && !isElectricSet) {
      dispatch(setLines("electric", electricLines));
    }
  }, [currentStep, isElectricSet, electricLines, dispatch]);

  // Gestione responsive per mobile accordion
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 991);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleHydraulicSelection = (num) => {
    setSelectedHydraulicLines(num);
    dispatch(setLines("hydraulic", num));
  };

  const handleHydraulicConfigure = () => {
    if (selectedHydraulicLines > 0) {
      dispatch(setCurrentStep(2));
    }
  };

  const handleElectricSelection = (num) => {
    setSelectedElectricLines(num);
    dispatch(setLines("electric", num));
  };

  const handleCompleteConfiguration = () => {
    dispatch(setCurrentStep(4));
  };

  const handleViewResults = () => {
    dispatch(setCurrentStep(4));
    history.push("/results");
  };
  const maxElectricLines = Math.max(0, totalSlots - hydraulicLines);

  const handleEditHydraulic = () => {
    dispatch(setCurrentStep(2));
  };

  const handleBackToElectric = () => {
    dispatch(setCurrentStep(3));
  };

  const hydraulicConfiguredLines = circles.filter(
    (circle) => circle?.type === "hydraulic"
  );

  const isHydraulicCircleComplete = (circle) => {
    if (!circle?.setting) {
      return false;
    }

    if (!hydraulicSelects.length) {
      return !!circle.setting;
    }

    return hydraulicSelects.every((select) => !!circle.setting?.[select.id]);
  };

  const completedHydraulicLines = hydraulicConfiguredLines.filter((circle) =>
    isHydraulicCircleComplete(circle)
  ).length;

  const remainingHydraulicToConfigure = Math.max(
    selectedHydraulicLines - completedHydraulicLines,
    0
  );

  const canCompleteConfiguration =
    selectedHydraulicLines > 0 && remainingHydraulicToConfigure === 0;

  // Helper per ottenere il titolo di ogni step (per accordion mobile)
  const getStepTitle = (stepNum) => {
    switch (stepNum) {
      case 1:
        return "How many hydraulic lines do you need?";
      case 2:
        return "Configure Hydraulic Lines";
      case 3:
        return "How many Electric lines?";
      case 4:
        return "Configuration Complete";
      default:
        return "";
    }
  };

  // Helper per verificare se uno step è completato (per accordion mobile)
  const isStepCompleted = (stepNum) => {
    switch (stepNum) {
      case 1:
        return hydraulicLines > 0;
      case 2:
        return (
          completedHydraulicLines === hydraulicLines &&
          isHydraulicSet &&
          hydraulicLines > 0
        );
      case 3:
        return isElectricSet && electricLines >= 0;
      case 4:
        return false;
      default:
        return false;
    }
  };

  // Helper per determinare quali step sono visibili nell'accordion
  // Mostra solo: step completati + step corrente attivo
  const getVisibleSteps = () => {
    const steps = [1, 2, 3, 4];
    const visibleSteps = [];

    for (const step of steps) {
      if (isStepCompleted(step)) {
        // Aggiungi tutti gli step completati
        visibleSteps.push(step);
      } else if (step === currentStep) {
        // Aggiungi lo step corrente anche se non completato
        visibleSteps.push(step);
        break; // Fermati qui, non mostrare step futuri
      }
    }

    return visibleSteps;
  };

  const renderCirclesGrid = () => {
    const displayCircles = [...circles];

    return (
      <div className="configurator__circles-grid">
        {displayCircles.map((circle, i) => {
          const number = i + 1;
          const isHydraulic = circle?.type === "hydraulic";
          const isElectric = circle?.type === "electric";
          const isConfigured = circle?.setting;

          return (
            <div
              key={`circle_${i}`}
              className={`circle ${isHydraulic ? "hydraulic" : ""} ${
                isElectric ? "electric" : ""
              } ${isConfigured ? "configured" : ""}`}
            >
              <div className="circle__number">{number}</div>
              {isConfigured && (
                <Icon className="circle__check" path={mdiCheck} />
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <>
      <Navbar
        title="Multiconfigurator"
        right={
          <button
            className="btn table primary"
            onClick={() => history.push("/catalogue")}
          >
            View Catalog
          </button>
        }
      />
      <StepsConfig />
      <div className="configurator-view">
        {isMobile ? (
          // LAYOUT MOBILE CON ACCORDION
          <div className="step-content mobile-accordion-view">
            <div className="step-left">
              {renderCirclesGrid()}
              <ConfigurationCode />
            </div>
            <div className="step-right">
              <div className="steps-accordion-wrapper">
                {getVisibleSteps().map((stepNum) => (
                  <div
                    key={`step_${stepNum}`}
                    className={`accordion-step ${
                      currentStep === stepNum ? "active" : ""
                    } ${isStepCompleted(stepNum) ? "completed" : ""}`}
                  >
                    <div
                      className={`accordion-header ${
                        isStepCompleted(stepNum) && stepNum !== currentStep
                          ? "clickable"
                          : ""
                      }`}
                      onClick={() => {
                        // Permetti click solo se lo step è completato
                        if (isStepCompleted(stepNum)) {
                          dispatch(setCurrentStep(stepNum));
                        }
                      }}
                    >
                      <h3>{getStepTitle(stepNum)}</h3>
                      {isStepCompleted(stepNum) && (
                        <div className="check-circle">
                          <Icon path={mdiCheck} size={0.5} />
                        </div>
                      )}
                    </div>
                    {currentStep === stepNum && stepNum === 1 && (
                      <div className="accordion-body">
                        <div className="selection-panel">
                          <div className="hydraulic-lines-count">
                            {selectedHydraulicLines} Hydraulic Line
                            {selectedHydraulicLines !== 1 ? "s" : ""}
                          </div>
                          <div className="number-selector">
                            {[...Array(10)].map((_, i) => {
                              const value = i + 1;
                              const isDisabled = value > maxHydraulicLines;
                              return (
                                <div
                                  key={i}
                                  className={`number-option ${
                                    selectedHydraulicLines === value
                                      ? "selected"
                                      : ""
                                  } ${isDisabled ? "disabled" : ""}`}
                                  onClick={() =>
                                    !isDisabled &&
                                    handleHydraulicSelection(value)
                                  }
                                >
                                  {value}
                                </div>
                              );
                            })}
                          </div>
                          <p className="note">
                            The hydraulic lines presented in this configurator
                            are intended for use with hydraulic oil only; for
                            use with other fluids, please contact Stucchi
                            support.
                          </p>
                          <button
                            className="btn btn-primary"
                            onClick={handleHydraulicConfigure}
                            disabled={selectedHydraulicLines === 0}
                          >
                            Configure Hydraulic lines
                          </button>
                        </div>
                      </div>
                    )}
                    {currentStep === stepNum && stepNum === 2 && (
                      <div className="accordion-body">
                        <ConfigurationPanel />
                      </div>
                    )}
                    {currentStep === stepNum && stepNum === 3 && (
                      <div className="accordion-body">
                        <div className="selection-panel electric-mode">
                          <div className="number-selector">
                            {[...Array(11)].map((_, i) => {
                              const isDisabled = i > maxElectricLines;
                              return (
                                <div
                                  key={i}
                                  className={`number-option ${
                                    selectedElectricLines === i
                                      ? "selected"
                                      : ""
                                  } ${isDisabled ? "disabled" : ""}`}
                                  onClick={() =>
                                    !isDisabled && handleElectricSelection(i)
                                  }
                                >
                                  {i}
                                </div>
                              );
                            })}
                          </div>
                          <p className="note">
                            The hydraulic lines presented in this configurator
                            are intended for use with hydraulic oil only; for
                            use with other fluids, please contact Stucchi
                            support.
                          </p>
                          <div className="button-group">
                            <button
                              className="btn btn-outline"
                              onClick={handleEditHydraulic}
                            >
                              Edit Hydraulic lines
                            </button>
                            <button
                              className="btn btn-primary"
                              onClick={handleCompleteConfiguration}
                              disabled={!canCompleteConfiguration}
                            >
                              Complete configuration
                            </button>
                          </div>
                          {!canCompleteConfiguration && (
                            <p className="validation-message">
                              {remainingHydraulicToConfigure === 1
                                ? "Complete the remaining hydraulic line before continuing."
                                : `Complete the remaining ${remainingHydraulicToConfigure} hydraulic lines before continuing.`}
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                    {currentStep === stepNum && stepNum === 4 && (
                      <div className="accordion-body">
                        <div className="selection-panel">
                          <p className="note success-note">
                            Review your setup or go back to adjust the electric
                            lines before seeing the final results.
                          </p>
                          <div className="button-group">
                            <button
                              className="btn btn-outline"
                              onClick={handleBackToElectric}
                            >
                              Edit Electric lines
                            </button>
                            <button
                              className="btn btn-primary"
                              onClick={handleViewResults}
                            >
                              See results
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          // LAYOUT DESKTOP ORIGINALE
          <>
            {/* STEP 1: Select Hydraulic Lines */}
            {currentStep === 1 && (
              <div className="step-content step-select-hydraulic">
                <div className="step-left">
                  {renderCirclesGrid()}
                  <ConfigurationCode />
                </div>
                <div className="step-right">
                  <div className="selection-panel">
                    <h2>How many hydraulic lines do you need?</h2>
                    <div className="hydraulic-lines-count">
                      {selectedHydraulicLines} Hydraulic Line
                      {selectedHydraulicLines !== 1 ? "s" : ""}
                    </div>
                    <div className="number-selector">
                      {[...Array(10)].map((_, i) => {
                        const value = i + 1;
                        const isDisabled = value > maxHydraulicLines;
                        return (
                          <div
                            key={i}
                            className={`number-option ${
                              selectedHydraulicLines === value ? "selected" : ""
                            } ${isDisabled ? "disabled" : ""}`}
                            onClick={() =>
                              !isDisabled && handleHydraulicSelection(value)
                            }
                          >
                            {value}
                          </div>
                        );
                      })}
                    </div>
                    <p className="note">
                      The hydraulic lines presented in this configurator are
                      intended for use with hydraulic oil only; for use with
                      other fluids, please contact Stucchi support.
                    </p>
                    <button
                      className="btn btn-primary"
                      onClick={handleHydraulicConfigure}
                      disabled={selectedHydraulicLines === 0}
                    >
                      Configure Hydraulic lines
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* STEP 2: Configure Hydraulic Lines */}
            {currentStep === 2 && (
              <>
                <div className="step-content step-configure-hydraulic">
                  <div className="step-left">
                    {renderCirclesGrid()}
                    <ConfigurationCode />
                  </div>
                  <div className="step-right">
                    <div className="selection-panel viewer-mode">
                      <h2>How many hydraulic lines do you need?</h2>
                      <div className="hydraulic-lines-count">
                        {selectedHydraulicLines} Hydraulic Line
                        {selectedHydraulicLines !== 1 ? "s" : ""}
                      </div>
                      <div className="number-selector disabled">
                        {[...Array(10)].map((_, i) => (
                          <div
                            key={i}
                            className={`number-option ${
                              selectedHydraulicLines === i + 1 ? "selected" : ""
                            }`}
                          >
                            {i + 1}
                          </div>
                        ))}
                      </div>
                      <p className="note">
                        The hydraulic lines presented in this configurator are
                        intended for use with hydraulic oil only; for use with
                        other fluids, please contact Stucchi support.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="configuration-panel-wrapper">
                  <ConfigurationPanel />
                </div>
              </>
            )}

            {/* STEP 3: Select Electric Lines */}
            {currentStep === 3 && (
              <div className="step-content step-select-electric">
                <div className="step-left">
                  {renderCirclesGrid()}
                  <ConfigurationCode />
                </div>
                <div className="step-right">
                  <div className="selection-panel electric-mode">
                    <h2>How many Electric lines?</h2>
                    <div className="number-selector">
                      {[...Array(11)].map((_, i) => {
                        const isDisabled = i > maxElectricLines;
                        return (
                          <div
                            key={i}
                            className={`number-option ${
                              selectedElectricLines === i ? "selected" : ""
                            } ${isDisabled ? "disabled" : ""}`}
                            onClick={() =>
                              !isDisabled && handleElectricSelection(i)
                            }
                          >
                            {i}
                          </div>
                        );
                      })}
                    </div>
                    <p className="note">
                      The hydraulic lines presented in this configurator are
                      intended for use with hydraulic oil only; for use with
                      other fluids, please contact Stucchi support.
                    </p>
                    <div className="button-group">
                      <button
                        className="btn btn-outline"
                        onClick={handleEditHydraulic}
                      >
                        Edit Hydraulic lines
                      </button>
                      <button
                        className="btn btn-primary"
                        onClick={handleCompleteConfiguration}
                        disabled={!canCompleteConfiguration}
                      >
                        Complete configuration
                      </button>
                    </div>
                    {!canCompleteConfiguration && (
                      <p className="validation-message">
                        {remainingHydraulicToConfigure === 1
                          ? "Complete the remaining hydraulic line before continuing."
                          : `Complete the remaining ${remainingHydraulicToConfigure} hydraulic lines before continuing.`}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* STEP 4: Done, Continue */}
            {currentStep === 4 && (
              <>
                <div className="step-content step-done">
                  <div className="step-left">
                    {renderCirclesGrid()}
                    <ConfigurationCode />
                  </div>
                  <div className="step-right">
                    <div className="selection-panel">
                      <h2>Configuration Complete</h2>
                      <p className="note success-note">
                        Review your setup or go back to adjust the electric
                        lines before seeing the final results.
                      </p>
                      <div className="button-group">
                        <button
                          className="btn btn-outline"
                          onClick={handleBackToElectric}
                        >
                          Edit Electric lines
                        </button>
                        <button
                          className="btn btn-primary"
                          onClick={handleViewResults}
                        >
                          See results
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default Configurator;
